/**
 * Rest-space inversion — ADR-028 E3's derivation half, the exact part.
 *
 * A corrective morph target displaces the BIND mesh, before skinning. But the
 * shape we can measure — the difference between what the elbow should look
 * like at θ and what linear blend skinning actually produces — lives in POSED
 * space. This module carries a posed correction back to the bind mesh, which
 * is the only frame a morph target may speak in.
 *
 * The math is exact rather than approximate, because skinning is AFFINE per
 * vertex. three composes it as (SkinnedMesh.applyBoneTransform, verified
 * against three 0.185.1):
 *
 *     A_v = bindMatrixInverse · (Σ wᵢ · boneMatrixWorldᵢ · boneInverseᵢ) · bindMatrix
 *     P   = A_v · R
 *
 * so displacing the rest position by d moves the skinned position by the
 * LINEAR part of A_v alone — the translation cancels in the difference:
 *
 *     skin(R + d) − skin(R) = L_v · d          where L_v = the 3×3 of A_v
 *
 * Therefore the rest-space delta that produces a wanted posed correction D is
 * simply `d = L_v⁻¹ · D`. No optimisation, no iteration, no approximation.
 *
 * It refuses when L_v is singular instead of returning three's zero matrix
 * (Matrix3.invert() silently zeroes a singular matrix — a delta of exactly
 * zero everywhere would look like "this vertex needed no correction", which is
 * the most expensive kind of wrong here: an entire crease quietly missing).
 *
 * Depends on: three. Depended on by: the crease derivation (delta-mush diff).
 */
import { Matrix3, Matrix4, Vector3, Vector4, type SkinnedMesh } from 'three';

/**
 * Below this |det| the blended matrix carries no invertible shape — a vertex
 * with no skin weight at all, or one whose bones collapse it flat. Skinning
 * matrices are products of rigid transforms and a bind matrix, so a healthy
 * determinant sits near the cube of the rig's scale; this is far beneath any
 * real character and well above float noise.
 */
const SINGULAR_DET = 1e-12;

export class RestSpaceDeltaError extends Error {
  constructor(readonly vertex: number, reason: string) {
    super(`Cannot carry a posed correction to rest space at vertex ${vertex}: ${reason}`);
  }
}

const _bone = new Matrix4();
const _blend = new Matrix4();
const _affine = new Matrix4();
const _linear = new Matrix3();
const _transpose = new Matrix3();
const _normal = new Matrix3();
const _skinIndex = new Vector4();
const _skinWeight = new Vector4();

/**
 * The vertex's blended skinning transform `A_v`, in the mesh's local space —
 * the same composition three skins with, including both bind matrices.
 */
export function blendedSkinMatrix(mesh: SkinnedMesh, vertex: number, target = new Matrix4()): Matrix4 {
  const geometry = mesh.geometry;
  const si = geometry.getAttribute('skinIndex');
  const sw = geometry.getAttribute('skinWeight');
  if (si === undefined || sw === undefined) {
    throw new RestSpaceDeltaError(vertex, 'the mesh carries no skinIndex/skinWeight');
  }
  _skinIndex.fromBufferAttribute(si as never, vertex);
  _skinWeight.fromBufferAttribute(sw as never, vertex);
  // Σ wᵢ · (boneMatrixWorld · boneInverse), accumulated element-wise: a
  // weighted SUM of matrices is not a product, so it cannot be composed with
  // Matrix4's multiply.
  _blend.elements.fill(0);
  for (let i = 0; i < 4; i += 1) {
    const weight = _skinWeight.getComponent(i);
    if (weight === 0) continue;
    const boneIndex = _skinIndex.getComponent(i);
    const bone = mesh.skeleton.bones[boneIndex];
    const inverse = mesh.skeleton.boneInverses[boneIndex];
    if (bone === undefined || inverse === undefined) {
      throw new RestSpaceDeltaError(vertex, `influence ${i} names bone ${boneIndex}, which the skeleton lacks`);
    }
    _bone.multiplyMatrices(bone.matrixWorld, inverse);
    for (let e = 0; e < 16; e += 1) _blend.elements[e]! += _bone.elements[e]! * weight;
  }
  target.multiplyMatrices(_blend, mesh.bindMatrix);
  return target.premultiply(mesh.bindMatrixInverse);
}

/**
 * Carry a POSED correction `D` at one vertex back to the rest-space delta a
 * morph target must hold. Exact: `d = L_v⁻¹ · D`.
 *
 * The mesh must already be posed — `updateMatrixWorld` run at the sample angle
 * — because `A_v` reads the bones' current world matrices. Feeding it a rest
 * pose returns the correction unchanged, which is correct and useless.
 */
export function restSpaceDelta(
  mesh: SkinnedMesh,
  vertex: number,
  posed: Vector3,
  target = new Vector3(),
  damping = 0,
): Vector3 {
  blendedSkinMatrix(mesh, vertex, _affine);
  _linear.setFromMatrix4(_affine);
  const det = _linear.determinant();
  if (!Number.isFinite(det) || Math.abs(det) < SINGULAR_DET) {
    throw new RestSpaceDeltaError(
      vertex,
      `the blended skin matrix is singular (|det| = ${Math.abs(det).toExponential(2)}) — ` +
        `an unweighted or collapsed vertex names no correction, and a silent zero would read as "needed none"`,
    );
  }
  if (damping <= 0) return target.copy(posed).applyMatrix3(_linear.invert());

  // DAMPED (Tikhonov) solve: d = (LᵀL + λ²I)⁻¹ Lᵀ D.
  //
  // The exact inverse is exact, and that is the danger. Where skinning
  // collapses hardest — which is exactly the crease — the blend of two very
  // different rotations is nearly singular, so L⁻¹ amplifies: measured on
  // amari at 135°, a correction of a few millimetres asked for a 144 mm
  // rest-space delta. Such a delta is right AT THE SAMPLE ANGLE and wild at
  // every other angle the ramp drives it through, because a morph target is
  // applied across a RANGE, not at one pose.
  //
  // Damping bounds that. Well-conditioned vertices are barely touched (λ is
  // small against their singular values); near-singular ones are suppressed
  // toward zero instead of exploding. The cost is honest and local: those
  // vertices get LESS correction than the reference asks, rather than a
  // correction nobody can use.
  const e = _linear.elements;
  const traceLtL = e[0]! * e[0]! + e[1]! * e[1]! + e[2]! * e[2]! + e[3]! * e[3]! + e[4]! * e[4]! + e[5]! * e[5]! + e[6]! * e[6]! + e[7]! * e[7]! + e[8]! * e[8]!;
  const lambdaSq = damping * damping * (traceLtL / 3);
  _transpose.copy(_linear).transpose();
  _normal.copy(_transpose).multiply(_linear);
  _normal.elements[0]! += lambdaSq;
  _normal.elements[4]! += lambdaSq;
  _normal.elements[8]! += lambdaSq;
  return target.copy(posed).applyMatrix3(_transpose).applyMatrix3(_normal.invert());
}
